import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Search, Menu, X, Heart } from "lucide-react";
import { useAuth } from "../App";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { guestCartCount } from "../utils/guestCart";
import AuthModal from "./AuthModal";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const searchRef = useRef(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      // Show guest cart count from localStorage
      setCartCount(guestCartCount());
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
        setSearchResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchCart = async () => {
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      const count = data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      setCartCount(count);
    } catch (error) {
      // silently fail
    }
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const { data } = await axios.get(`${API}/search?q=${encodeURIComponent(q)}`);
      setSearchResults(data.slice(0, 5));
    } catch (error) {
      setSearchResults([]);
    }
  };

  const handleSearchSelect = (productId) => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    navigate(`/product/${productId}`);
  };

  return (
    <>
    <nav className="sticky top-0 z-50 glass" data-testid="main-navbar">
      <div className="px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="cormorant text-[1.8rem] font-light italic" data-testid="logo-link">
            RIVORA JEWELS
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-sm uppercase tracking-wide text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="home-link">Home</Link>
            <Link to="/shop" className="text-sm uppercase tracking-wide text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="shop-link">Shop</Link>
            <Link to="/story" className="text-sm uppercase tracking-wide text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="story-link">Story</Link>
            <Link to="/contact" className="text-sm uppercase tracking-wide text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="contact-link">Contact</Link>
            {user?.role === "admin" && (
              <Link to="/admin" className="text-sm uppercase tracking-wide text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="admin-link">Admin</Link>
            )}
          </div>

          <div className="flex items-center space-x-5">
            {/* Search */}
            <div className="relative" ref={searchRef}>
              <button onClick={() => setSearchOpen(!searchOpen)} className="text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="search-button">
                <Search size={20} />
              </button>
              {searchOpen && (
                <div className="absolute right-0 top-10 w-80 bg-white border border-zinc-200 shadow-xl z-50" data-testid="search-dropdown">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search jewellery..."
                    className="w-full px-4 py-3 text-sm border-b border-zinc-200 focus:outline-none"
                    autoFocus
                    data-testid="search-input"
                  />
                  {searchResults.length > 0 && (
                    <div className="max-h-64 overflow-y-auto">
                      {searchResults.map((product) => (
                        <button
                          key={product.product_id}
                          onClick={() => handleSearchSelect(product.product_id)}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-zinc-50 text-left"
                          data-testid={`search-result-${product.product_id}`}
                        >
                          <img src={product.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">{product.name}</p>
                            <p className="text-xs text-zinc-500">₹{product.price.toLocaleString()}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Wishlist */}
            {user && (
              <Link to="/wishlist" className="text-zinc-600 hover:text-zinc-900 transition-colors hidden md:block" data-testid="wishlist-link">
                <Heart size={20} />
              </Link>
            )}

            {/* User */}
            {user ? (
              <div className="relative group">
                <button className="text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="user-menu-button">
                  <User size={20} />
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white border border-zinc-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all" data-testid="user-dropdown">
                  <Link to="/account" className="block px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100" data-testid="account-link">My Account</Link>
                  <Link to="/wishlist" className="block px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100" data-testid="wishlist-menu-link">Wishlist</Link>
                  {user.role === "admin" && <Link to="/admin" className="block px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100">Admin Panel</Link>}
                  <button onClick={logout} className="block w-full text-left px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100" data-testid="logout-button">Logout</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="text-zinc-600 hover:text-zinc-900 transition-colors"
                data-testid="login-link"
                aria-label="Sign in"
              >
                <User size={20} />
              </button>
            )}

            {/* Cart */}
            <Link to="/cart" className="relative text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="cart-link">
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-zinc-900 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>

            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-button">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-zinc-200" data-testid="mobile-menu">
            <Link to="/" className="block py-2 text-sm uppercase tracking-wide" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link to="/shop" className="block py-2 text-sm uppercase tracking-wide" onClick={() => setMobileMenuOpen(false)}>Shop</Link>
            <Link to="/story" className="block py-2 text-sm uppercase tracking-wide" onClick={() => setMobileMenuOpen(false)}>Story</Link>
            <Link to="/contact" className="block py-2 text-sm uppercase tracking-wide" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            {user && <Link to="/wishlist" className="block py-2 text-sm uppercase tracking-wide" onClick={() => setMobileMenuOpen(false)}>Wishlist</Link>}
            {user?.role === "admin" && (
              <Link to="/admin" className="block py-2 text-sm uppercase tracking-wide" onClick={() => setMobileMenuOpen(false)}>Admin</Link>
            )}
          </div>
        )}
      </div>
    </nav>

    {/* Auth modal — shown when guest clicks the user icon */}
    {showAuthModal && (
      <AuthModal
        defaultMode="signin"
        onSuccess={() => {
          setShowAuthModal(false);
        }}
        onClose={() => setShowAuthModal(false)}
      />
    )}
    </>
  );
}
